import { DOMParser } from 'linkedom';

import type { HarnessAgentName } from './plan';
import { parseJsonlToSuggestionList } from './suggestion-code-parser';
import { aiMimeTypes } from '../constants';
import { ListMarkerStyleName } from '../slate-api';
import type { UUID } from '../types';

/**
 * Helpers for detecting and parsing writer-node (drafter / editor)
 * artifacts. Used by:
 *   - the chat-panel sub-agent card status (so a "Completed" activity
 *     that returned only an apology renders as Failed);
 *   - the server-side `executeNodeActivity` post-completion step
 *     (parses fences to build the structured `artifact` it attaches
 *     to the `TextNodeOutput` and the `AgentMessageDocumentJoin` row).
 *
 * Convention:
 *   - drafter must emit at least one non-empty `<aline-draft-document>`
 *     wrapper.
 *   - editor must emit at least one non-empty edit fence: harness V2
 *     streams `jsonl-redline-edit`; legacy V1 chat used
 *     `csv-suggestion-list` / `jsonl-suggestion-list`.
 *
 * Other agents (researchers, analyzer) are not gated by this check —
 * their job is prose, not a structured artifact.
 */

/**
 * Hoisted regex source strings — `new RegExp` is rebuilt in the
 * suggestion-fence helpers so each call gets a fresh `lastIndex`
 * (stateful global regexes are a known footgun when shared across
 * callers — `.exec` / `.matchAll` then resume from wherever the last
 * call left off).
 */
const HTML_DRAFT_WRAPPER_RE =
  /<aline-draft-document\b([^>]*)>([\s\S]*?)<\/aline-draft-document>/gim;
const EDITOR_ARTIFACT_FENCE_SRC = `\`\`\`(?:${aiMimeTypes.jsonlSuggestionList}|${aiMimeTypes.csvSuggestionList}|${aiMimeTypes.jsonlRedlineEdit})\\s*\\n([\\s\\S]*?)\\n\`\`\``;
const PLAYBOOK_EDIT_FENCE_SRC = `\`\`\`${aiMimeTypes.jsonlPlaybookEdit}\\s*\\n([\\s\\S]*?)\\n\`\`\``;

export const ALINE_DRAFT_DATA_ATTRIBUTES = {
  artifactIndex: 'data-artifact-index',
  documentId: 'data-document-id',
  documentTitle: 'data-document-title',
  listId: 'data-aline-list-id',
  listLevel: 'data-aline-list-level',
  listStyle: 'data-aline-list-style',
  pageBreak: 'data-aline-page-break',
  preserveEmptyColumn: 'data-aline-preserve-empty-column',
} as const;

export function getAlineDraftListMarkerStyleName(
  value: string | null | undefined
): ListMarkerStyleName {
  switch (value?.trim().toLowerCase()) {
    case 'black-circle':
      return ListMarkerStyleName.BLACK_CIRCLE;
    case 'black-square':
      return ListMarkerStyleName.BLACK_SQUARE;
    case 'bullet':
      return ListMarkerStyleName.BULLET;
    case 'decimal':
    case 'number':
      return ListMarkerStyleName.DECIMAL;
    case 'decimal-leading-zero':
      return ListMarkerStyleName.DECIMAL_LEADING_ZERO;
    case 'decimal-section':
      return ListMarkerStyleName.DECIMAL_SECTION;
    case 'letter':
    case 'lower-latin':
      return ListMarkerStyleName.LOWER_LATIN;
    case 'lower-roman':
    case 'roman':
      return ListMarkerStyleName.LOWER_ROMAN;
    case 'upper-latin':
    case 'upper-letter':
      return ListMarkerStyleName.UPPER_LATIN;
    case 'upper-roman':
      return ListMarkerStyleName.UPPER_ROMAN;
    case 'white-circle':
      return ListMarkerStyleName.WHITE_CIRCLE;
    default:
      return ListMarkerStyleName.DECIMAL;
  }
}

export function parseHtmlElementAttributes(tagName: string, attributeText: string) {
  const document = new DOMParser().parseFromString(
    `<${tagName} ${attributeText}></${tagName}>`,
    'text/html'
  );
  const element = document.querySelector(tagName);
  const attributes = new Map<string, string>();
  if (!element) return attributes;

  for (let index = 0; index < element.attributes.length; index += 1) {
    const attribute = element.attributes.item(index);
    if (!attribute) continue;

    attributes.set(attribute.name.toLowerCase(), attribute.value);
  }

  return attributes;
}

function* iterateEditorArtifactFenceBodies(nodeText: string): Generator<string> {
  const re = new RegExp(EDITOR_ARTIFACT_FENCE_SRC, 'gm');
  for (const match of nodeText.matchAll(re)) {
    const body = (match[1] ?? '').trim();
    if (body.length > 0) yield body;
  }
}

export interface ParsedHtmlDraftArtifact {
  artifactIndex: number;
  documentId: UUID;
  html: string;
  title: string;
}

export function parseHtmlDraftArtifacts(nodeText: string): ParsedHtmlDraftArtifact[] {
  const artifacts: ParsedHtmlDraftArtifact[] = [];

  for (const wrapperMatch of nodeText.matchAll(HTML_DRAFT_WRAPPER_RE)) {
    const attributes = parseHtmlElementAttributes('aline-draft-document', wrapperMatch[1] ?? '');
    const documentId = attributes.get(ALINE_DRAFT_DATA_ATTRIBUTES.documentId);
    if (!documentId) continue;

    const html = (wrapperMatch[2] ?? '').trim();
    if (!html) continue;

    const artifactIndex = Number(
      attributes.get(ALINE_DRAFT_DATA_ATTRIBUTES.artifactIndex) ?? artifacts.length
    );
    artifacts.push({
      artifactIndex: Number.isFinite(artifactIndex) ? artifactIndex : artifacts.length,
      documentId,
      html,
      title: getHtmlDraftTitle(attributes.get(ALINE_DRAFT_DATA_ATTRIBUTES.documentTitle)),
    });
  }

  return artifacts;
}

function getHtmlDraftTitle(attributeTitle: string | null | undefined): string {
  return attributeTitle?.trim() || 'Untitled Document';
}

/** True when the editor node text contains a non-empty edit fence body. */
export function hasNonEmptyEditorArtifact(nodeText: string): boolean {
  for (const _ of iterateEditorArtifactFenceBodies(nodeText)) return true;
  return false;
}

/**
 * True when a playbook editor node emitted at least one op line. A `note` op
 * counts: it is the agent's way of saying why nothing was changed, which is an
 * answer rather than a failure. So does an op the server refused, which
 * reaches the fence rewritten as a note saying why.
 *
 * No lines at all reads as a failure on purpose. Changing nothing is a valid
 * response, but the prompt requires a note with it, and a reply with neither
 * an edit nor a note has told the user nothing.
 */
export function hasNonEmptyPlaybookEditArtifact(nodeText: string): boolean {
  const re = new RegExp(PLAYBOOK_EDIT_FENCE_SRC, 'gm');
  for (const match of nodeText.matchAll(re)) {
    if ((match[1] ?? '').trim().length > 0) return true;
  }
  return false;
}

/**
 * Pull every parsed suggestion group id out of an editor node's text,
 * in fence order. The redlines structured artifact keeps those group ids;
 * artifact links only use the resolved document id.
 */
export function parseSuggestionGroupIds(nodeText: string): {
  documentId: string | null;
  groupIds: string[];
} {
  const groupIds: string[] = [];
  let documentId: string | null = null;
  for (const body of iterateEditorArtifactFenceBodies(nodeText)) {
    for (const item of parseJsonlToSuggestionList(body)) {
      if (item.type === 'edit') {
        if (documentId == null) documentId = item.documentId;
        groupIds.push(item.groupId);
      }
    }
  }
  return { documentId, groupIds };
}

const ARTIFACT_PRESENCE_BY_AGENT: Partial<Record<HarnessAgentName, (text: string) => boolean>> = {
  drafter: text => {
    const htmlDrafts = parseHtmlDraftArtifacts(text);
    return htmlDrafts.some(draft => draft.html.length > 0);
  },
  editor: text => hasNonEmptyEditorArtifact(text),
  playbookEditor: text => hasNonEmptyPlaybookEditArtifact(text),
};

/**
 * True if this writer node finished without producing its expected
 * structured artifact (drafter without a draft fence, or editor with
 * only an apology message and no edit fences). Returns false for
 * non-writers and for writers whose artifacts are present.
 */
export function getIsWriterArtifactMissing(agent: HarnessAgentName, nodeText: string): boolean {
  const artifactPresent = ARTIFACT_PRESENCE_BY_AGENT[agent];
  if (!artifactPresent) return false;
  return !artifactPresent(nodeText);
}
